import type { EventType, QuestionType } from "@mmm/contracts";
import { addDays, toDateKey, todayKey } from "@mmm/shared";
import { type MessageKey } from "@mmm/ui";
// UI-only presentation values — not part of the wire contract, so they live
// here rather than @mmm/contracts. Domain enums (PALETTE, EVENT_TYPES,
// QUESTION_TYPES, etc.) come from @mmm/contracts directly.

export const EVENT_TYPE_COLORS: Record<EventType, { text: string; bg: string }> = {
  exam: { text: "var(--mmm-danger)", bg: "rgba(190,40,60,0.1)" },
  assignment: { text: "var(--mmm-purple)", bg: "rgba(106,40,192,0.1)" },
  deadline: { text: "var(--mmm-gold)", bg: "rgba(184,144,26,0.1)" },
  test: { text: "#c44820", bg: "rgba(196,72,32,0.1)" },
  reminder: { text: "#1e7a8c", bg: "rgba(30,122,140,0.1)" },
  custom: { text: "#3a47c8", bg: "rgba(58,71,200,0.1)" },
};

// Message keys for the wire enum values (which are lowercase/camelCase
// identifiers). Never render the raw enum value to users — render
// t(EVENT_TYPE_LABEL_KEYS[type]) so the label follows the UI language.
export const EVENT_TYPE_LABEL_KEYS: Record<EventType, MessageKey> = {
  test: "eventType.test",
  assignment: "eventType.assignment",
  exam: "eventType.exam",
  deadline: "eventType.deadline",
  reminder: "eventType.reminder",
  custom: "eventType.custom",
};

export const QUESTION_TYPE_LABEL_KEYS: Record<QuestionType, MessageKey> = {
  multipleChoice: "questionType.multipleChoice",
  multiSelect: "questionType.multiSelect",
  trueFalse: "questionType.trueFalse",
  freeText: "questionType.freeText",
};

export const DEFAULT_NODE_WIDTH = 180;
export const DEFAULT_NODE_HEIGHT = 72;

// Home "Upcoming" card: always show every event within the next
// UPCOMING_WITHIN_DAYS days, then backfill with the nearest events beyond that
// window until the list reaches UPCOMING_MAX (so the card is never sparse and,
// importantly, reflects ANY future event — which is what the empty-account
// getting-started check keys off).
export const UPCOMING_WITHIN_DAYS = 14;
export const UPCOMING_MAX = 10;

export function upcomingCutoffKey(): string {
  return addDays(todayKey(), UPCOMING_WITHIN_DAYS);
}

// Landing-screen query args — shared with the boot splash, which pre-warms
// these exact queries so screens paint complete on first render. No `to`
// bound: this fetches all future events (today onward), and the card slices
// them per the within-days + backfill rule above.
export function homeUpcomingArgs(): { from: string } {
  return { from: todayKey() };
}

/** The global calendar's initial visible range: the current month — computed
 * with the same toDateKey the screen uses, so the cache keys line up. */
export function currentMonthWindow(): { from: string; to: string } {
  const today = new Date();
  return {
    from: toDateKey(new Date(today.getFullYear(), today.getMonth(), 1)),
    to: toDateKey(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
  };
}
